import { useCallback, useContext, useMemo } from 'react';
import { Axis, DataContext } from '@visx/xychart';

import { computeTimeGranularity, createXAxisTimeFormatter } from '../../../utils/formatters';
import { extractDomainBounds, getUniqueXValues } from '../../../utils/scaleHelpers';
import type { XAxisProps } from '../../types/XAxisProps';
import { getEstimatedCharWidth } from '../../utils/tickCalculation';
import { useChartContext } from '../../utils/useChartContext';
import {
  computeBandScaleTickValues,
  computeContinuousScaleTickValues,
  computeNumTicksForEvenSpacing,
  createScaleFunction,
  createTickFormatter,
  shouldHideXAxisLine,
} from '../../utils/xAxisHelpers';

/**
 * XAxis component for composable LineChart/ColumnChart.
 *
 * Thin wrapper around visx's Axis component with LaunchPad-specific defaults.
 * Reads configuration from context (which is extracted from these props by the parent chart).
 *
 * @example
 * ```tsx
 * <LineChart>
 *   <XAxis dataKey="timestamp" dataType="time" />
 *   <Series ... />
 * </LineChart>
 * ```
 */
export function XAxis({
  dataKey,
  dataType = 'time',
  orientation = 'bottom',
  tickFormat,
  maxNumTicks,
  label,
  labelOffset,
  tickStrategy = 'data-aligned',
  ...rest
}: XAxisProps) {
  // Read from context - values came from our own props via parent chart extraction
  const { xScaleType, xDataType, visibleData } = useChartContext();
  const dataContext = useContext(DataContext);

  // Determine if X-axis line should be hidden based on Y-axis position
  const hideAxisLine = useMemo(() => shouldHideXAxisLine(dataContext?.yScale), [dataContext?.yScale]);

  // Extract scale domain (works for both band and continuous scales)
  const scaleDomain = useMemo(() => {
    if (!dataContext?.xScale) {
      return undefined;
    }
    if ('domain' in dataContext.xScale && typeof dataContext.xScale.domain === 'function') {
      return dataContext.xScale.domain();
    }
    return undefined;
  }, [dataContext?.xScale]);

  // Get innerWidth from dataContext for responsive tick calculation
  const innerWidth = dataContext?.innerWidth;

  // For time data without custom formatter: get sorted timestamps for scale-adaptive formatting
  const uniqueXValuesForTime = useMemo(() => {
    if (xDataType !== 'time' || tickFormat) {
      return null;
    }
    if (!scaleDomain) {
      return null;
    }
    if (xScaleType === 'band') {
      const domain = scaleDomain as string[];
      const nums = domain.map((s) => Number(s)).filter((n) => !Number.isNaN(n));
      return [...new Set(nums)].sort((a, b) => a - b);
    }
    const bounds = extractDomainBounds(scaleDomain as [number | Date, number | Date]);
    if (!bounds) {
      return null;
    }
    return getUniqueXValues(visibleData, dataKey, bounds);
  }, [xDataType, tickFormat, xScaleType, scaleDomain, visibleData, dataKey]);

  // Create tick formatter (scale-adaptive for time when no custom formatter; else time/linear/band default)
  const tickFormatter = useMemo(() => {
    if (xDataType === 'time' && !tickFormat && uniqueXValuesForTime && uniqueXValuesForTime.length > 0) {
      const timeFormatter = createXAxisTimeFormatter(uniqueXValuesForTime, {});
      return (value: string | number): string => {
        const numValue = typeof value === 'string' ? Number(value) : value;
        return typeof numValue === 'number' && !Number.isNaN(numValue) ? timeFormatter(numValue) : String(value);
      };
    }
    return createTickFormatter(xDataType, tickFormat);
  }, [xDataType, tickFormat, uniqueXValuesForTime]);

  // Create scale wrapper function for overlap detection
  const scaleFunction = useMemo(() => createScaleFunction(dataContext?.xScale), [dataContext?.xScale]);

  // Compute tick values for data-aligned strategy
  // For evenly-spaced strategy, returns undefined to let visx generate nice round ticks
  const tickValues = useMemo(() => {
    if (tickStrategy === 'evenly-spaced') {
      return undefined;
    }

    if (!scaleDomain || !scaleFunction) {
      return undefined;
    }

    if (xScaleType === 'band') {
      return computeBandScaleTickValues(scaleDomain as string[], maxNumTicks, innerWidth, scaleFunction, tickFormatter);
    }

    return computeContinuousScaleTickValues(
      scaleDomain as [number | Date, number | Date],
      visibleData,
      dataKey,
      maxNumTicks,
      innerWidth,
      scaleFunction,
      tickFormatter,
    );
  }, [
    tickStrategy,
    xScaleType,
    maxNumTicks,
    scaleDomain,
    scaleFunction,
    visibleData,
    dataKey,
    innerWidth,
    tickFormatter,
  ]);

  // Calculate numTicks for evenly-spaced strategy (when tickValues is not set)
  const computedNumTicks = useMemo(() => {
    if (tickValues) {
      return undefined; // When tickValues are explicitly set, don't use numTicks
    }

    if (tickStrategy === 'evenly-spaced') {
      return computeNumTicksForEvenSpacing(xDataType, maxNumTicks, innerWidth, dataContext?.xScale);
    }

    return undefined; // Let visx use its default
  }, [tickValues, tickStrategy, xDataType, maxNumTicks, innerWidth, dataContext?.xScale]);

  // Use displayed tick values for boundary detection so the first *visible* tick on a new day shows date+time.
  // Reuse data-determined granularity from uniqueXValuesForTime so narrow charts (few ticks, large gaps)
  // don't switch to coarser labels (e.g. daily "Jan 15" instead of hourly time + boundary annotations).
  const displayFormatter = useMemo(() => {
    if (
      xDataType === 'time' &&
      !tickFormat &&
      tickValues &&
      tickValues.length > 0 &&
      uniqueXValuesForTime &&
      uniqueXValuesForTime.length > 0
    ) {
      const nums = tickValues
        .map((v) => (typeof v === 'string' ? Number(v) : v))
        .filter((n): n is number => typeof n === 'number' && !Number.isNaN(n))
        .sort((a, b) => a - b);
      if (nums.length > 0) {
        const granularity = computeTimeGranularity(uniqueXValuesForTime);
        const timeFmt = createXAxisTimeFormatter(nums, { granularity });
        return (value: string | number): string => {
          const numValue = typeof value === 'string' ? Number(value) : value;
          return typeof numValue === 'number' && !Number.isNaN(numValue) ? timeFmt(numValue) : String(value);
        };
      }
    }
    return tickFormatter;
  }, [xDataType, tickFormat, tickValues, tickFormatter, uniqueXValuesForTime]);

  // visx sets the x-axis scale range to [margin.left, width - margin.right], so tick `from.x`
  // values are in full SVG coordinate space (not inner-chart space). We must compare against
  // SVG-space boundaries, not `innerWidth` (which excludes the left margin).
  //
  // Both boundaries are the outer container edges (0 and total SVG width). Labels are allowed
  // to overlap the Y-axis margin area or right margin whitespace — we only prevent them from
  // bleeding outside the visible container entirely. dataContext.width is the measured container
  // width (visx uses a ResizeObserver internally), so it matches the outer container.
  const svgLeftBoundary = 0;
  const svgRightBoundary = dataContext?.width;

  // Returns 'start' / 'end' only when the centered label would overflow the chart boundary.
  const getTickTextAnchor = useCallback(
    (value: string | number, tickX: number): 'start' | 'middle' | 'end' => {
      if (svgRightBoundary === undefined) {
        return 'middle';
      }
      const halfWidth = (displayFormatter(value).length * getEstimatedCharWidth()) / 2;
      if (tickX - halfWidth < svgLeftBoundary) {
        return 'start';
      }
      if (tickX + halfWidth > svgRightBoundary) {
        return 'end';
      }
      return 'middle';
    },
    [svgLeftBoundary, svgRightBoundary, displayFormatter],
  );

  return (
    <Axis
      orientation={orientation}
      numTicks={computedNumTicks}
      tickValues={tickValues}
      tickLabelProps={(value, _index, ticks) => {
        const tickX = (ticks[_index] as { from?: { x?: number } } | undefined)?.from?.x ?? 0;
        return { textAnchor: getTickTextAnchor(value, tickX), dy: '0.25em' };
      }}
      label={label}
      tickFormat={displayFormatter as (value: number | string) => string}
      labelOffset={labelOffset ?? (label ? 40 : 0)}
      stroke="var(--lp-color-text-ui-secondary)"
      hideAxisLine={hideAxisLine}
      tickStroke="var(--lp-color-text-ui-secondary)"
      strokeWidth={1}
      {...rest}
    />
  );
}

XAxis.displayName = 'XAxis';
